import QRCodeStyling from 'qr-code-styling'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import usePaymentVerificator from '@features/payment/hooks/usePaymentVerificator'

import { useIsMobile } from '@shared/hooks/useIsMobile'
import { useLocalizedNavigate as useNavigate } from '@shared/lib/locale'

import './sbp-payment.scss'

type SBPPaymentProps = {
  qrUrl: string
  operationId: string
  verificationUrl: string
}

export default function SBPPayment ({
  qrUrl,
  operationId,
  verificationUrl,
}: SBPPaymentProps) {
  const { t } = useTranslation('payment')
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const qrCodeRef = useRef<HTMLDivElement>(null)
  const qrCodeInstance = useRef<QRCodeStyling | null>(null)

  const { verified, rejected } = usePaymentVerificator({ 
    operationId
  })

  // Initialize QR code
  useEffect(() => {
    if (!qrCodeRef.current || isMobile) return

    // Clear any existing QR code
    if (qrCodeRef.current) {
      qrCodeRef.current.innerHTML = ''
    }

    qrCodeInstance.current = new QRCodeStyling({
      width: 256,
      height: 256,
      data: qrUrl,
      margin: 10,
      qrOptions: {
        typeNumber: 0,
        mode: 'Byte',
        errorCorrectionLevel: 'Q',
      },
      imageOptions: {
        hideBackgroundDots: true,
        imageSize: 0.4,
        margin: 0,
      },
      dotsOptions: {
        type: 'rounded',
        color: '#000000',
      },
      backgroundOptions: {
        color: '#ffffff',
      },
      cornersSquareOptions: {
        type: 'extra-rounded',
        color: '#000000',
      },
      cornersDotOptions: {
        type: 'dot',
        color: '#000000',
      },
    })

    qrCodeInstance.current.append(qrCodeRef.current)

    return () => {
      if (qrCodeRef.current) {
        qrCodeRef.current.innerHTML = ''
      }
    }
  }, [ qrUrl, isMobile ])

  // Redirect when verification is complete
  useEffect(() => {
    if (verified || rejected) {
      navigate(verificationUrl)
    }
  }, [ verified, rejected, verificationUrl, navigate ])

  // On mobile, redirect immediately to the SBP URL
  useEffect(() => {
    if (isMobile && qrUrl) {
      window.location.href = qrUrl
    }
  }, [ isMobile, qrUrl ])

  if (isMobile) {
    return (
      <div className="sbp-payment sbp-payment--mobile">
        <div className="sbp-payment__spinner" />
        <p className="sbp-payment__message">{t('sbp.redirecting')}</p>
        <a
          className="sbp-payment__link"
          href={qrUrl}
          rel="noopener noreferrer"
        >
          {t('sbp.manual_redirect')}
        </a>
      </div>
    )
  }

  return (
    <div className="sbp-payment">
      <h2 className="sbp-payment__title">{t('sbp.title')}</h2>
      <p className="sbp-payment__description">{t('sbp.description')}</p>
      <div className="sbp-payment__qr" ref={qrCodeRef} />
      <a
        className="sbp-payment__button"
        href={qrUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('sbp.button')}
      </a>
    </div>
  )
}
